import { useQueries, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { AxiosResponse } from 'axios'

import { withServiceDetailsPoll } from '@/lib/queryPolling'
import { GenericObject } from '@/lib/definitions/generic.types'
import { ServiceService } from '@/lib/services/ServiceService'
import {
  BatchComponentMetricsSeriesResponse,
  BatchMetricsSeriesResponse,
  LoaDocument,
  MetricServiceKind,
} from '@/lib/definitions/api.types'
import {
  ProductComponentIdentifiers,
  ProductIdentifiers,
} from '@/lib/definitions/types'
import {
  GeoCircle,
  GeoRectangle,
} from '@/pages/ServiceWizard/ServiceWizard.types'

const serviceService = new ServiceService()

export const useLoadServices = (
  {
    serviceId,
    productId,
    serviceComponentId,
    includeDecommissioned,
    includeCancelled,
    includeResellerCustomers,
  }: {
    serviceId?: string | number
    serviceComponentId?: string | number
    productId?: ProductIdentifiers | ProductIdentifiers[]
    includeDecommissioned?: boolean
    includeCancelled?: boolean
    includeResellerCustomers?: boolean
  },
  params?: GenericObject
) =>
  useQuery({
    queryKey: [
      'fetchServices',
      serviceId,
      productId,
      serviceComponentId,
      includeDecommissioned,
      includeCancelled,
      includeResellerCustomers,
    ],
    queryFn: async () =>
      serviceService.getServices({
        serviceId,
        productId,
        serviceComponentId,
        includeDecommissioned,
        includeCancelled,
        includeResellerCustomers,
      }),
    select: (data) => data.data,
    retry: false,
    ...params,
  })

export const useLoadService = (
  serviceId: string | number,
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchServices', serviceId],
    queryFn: async () => serviceService.getService(serviceId),
    meta: {
      error: { message: 'Could not load this service' },
    },
    select: (data) => data.data,
    retry: false,
    ...params,
  })

export const useLoadServiceDetails = (
  serviceId: string | number,
  params?: GenericObject
) => useLoadService(serviceId, withServiceDetailsPoll(params))

export interface AggregatedCommitServiceIDs {
  portal_ids: string[]
  sids: string[]
  services?: { portal_id: string; sid: string; port_speed: number }[]
}

export const useLoadAggregatedCommitServiceIDs = (
  serviceId: string | number,
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchAggregatedCommitServices', serviceId],
    queryFn: async () =>
      serviceService.getAggregatedCommitServiceIDs(serviceId),
    meta: {
      error: { message: 'Could not load aggregated commit services' },
    },
    select: (data): AggregatedCommitServiceIDs => data.data,
    retry: false,
    ...params,
  })

export const useLoadAggregatedCommitServiceIDsForDetails = (
  serviceId: string | number,
  params?: GenericObject
) =>
  useLoadAggregatedCommitServiceIDs(serviceId, withServiceDetailsPoll(params))

export const useLoadServicesForDetails = (
  args: Parameters<typeof useLoadServices>[0],
  params?: GenericObject
) => useLoadServices(args, withServiceDetailsPoll(params))

export const useLoadServiceComponents = (
  {
    serviceId,
    excludeDependency,
    pco,
    dependencyId,
    includeServices,
  }: {
    serviceId?: string | number
    excludeDependency?: boolean
    pco?: ProductComponentIdentifiers | ProductComponentIdentifiers[]
    dependencyId?: string | number
    includeServices?: boolean
  },
  params?: GenericObject
) =>
  useQuery({
    queryKey: [
      'fetchServiceComponents',
      serviceId,
      excludeDependency,
      pco,
      dependencyId,
      includeServices,
    ],
    queryFn: async () =>
      serviceService.getServiceComponents({
        serviceId,
        excludeDependency,
        pco,
        dependencyId,
        includeServices,
      }),
    meta: {
      error: { message: 'Could not load this service' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadServiceComponentsForDetails = (
  args: Parameters<typeof useLoadServiceComponents>[0],
  params?: GenericObject
) => useLoadServiceComponents(args, withServiceDetailsPoll(params))

export const useLoadUsedVlans = (
  {
    serviceId,
  }: {
    serviceId: string | number
  },
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchUsedVlans', serviceId],
    queryFn: async () => serviceService.getUsedVlans(serviceId),
    meta: {
      error: { message: 'Could not load this service' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadServiceComponent = (
  serviceComponentId: string | number,
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchServiceComponent', serviceComponentId],
    queryFn: async () => serviceService.getServiceComponent(serviceComponentId),
    meta: {
      error: { message: 'Could not load this service component' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadServiceComponentForDetails = (
  serviceComponentId: string | number,
  params?: GenericObject
) => useLoadServiceComponent(serviceComponentId, withServiceDetailsPoll(params))

export const useLoadLocations = (
  queryParams?: {
    productId?: number | string
  },
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchLocations', queryParams?.productId],
    queryFn: async () => serviceService.getLocations(queryParams),
    retry: 2,
    refetchOnWindowFocus: false,
    meta: {
      error: { message: 'Could not load the locations' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useSearchLocations = (
  queryParams?: {
    text?: string
    name?: string
    country?: string
    withinCircle?: GeoCircle
    withinRectangle?: GeoRectangle
    allowsGRETunnels?: boolean
    productPID?: string
    portType?: string
    includeExtensions?: boolean
  },
  params?: GenericObject
) =>
  useQuery({
    queryKey: [
      'fetchLocations',
      queryParams?.text,
      queryParams?.name,
      queryParams?.country,
      queryParams?.withinCircle,
      queryParams?.withinRectangle,
      queryParams?.productPID,
      queryParams?.allowsGRETunnels,
      queryParams?.portType,
      queryParams?.includeExtensions,
    ],
    queryFn: async () => serviceService.searchLocations(queryParams),
    retry: 2,
    refetchOnWindowFocus: false,
    meta: {
      error: { message: 'Could not load the locations' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadPortTypes = () =>
  useQuery({
    queryKey: ['fetchPortTypes'],
    queryFn: async () => serviceService.getPortTypes(),
    retry: 2,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    meta: {
      error: { message: 'Could not load the port types' },
    },
    select: (data) => data.data,
  })

export const useLoadProducts = (params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchProducts'],
    queryFn: async () => serviceService.getProducts(),
    retry: 2,
    refetchOnWindowFocus: false,
    meta: {
      error: { message: 'Could not load the products' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadProjects = (params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchProjects'],
    queryFn: async () => serviceService.getProjects(),
    retry: 2,
    refetchOnWindowFocus: false,
    meta: {
      error: { message: 'Could not load the projects' },
    },
    select: (data) => data.data,
    ...params,
  })

export const useLoadLoaDocuments = (
  { serviceId }: { serviceId?: string | number },
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchLoaDocuments'],
    queryFn: async () => serviceService.getLoaDocuments({ serviceId }),
    meta: {
      error: { message: 'Could not load the LOA documents' },
    },
    select: (data) =>
      data.data.map((document: LoaDocument) => ({
        ...document,
        url: `${
          import.meta.env.VITE_PORTAL_API_HOST || ''
        }/loa-document/download/${document.id}?token=${document.token}`,
      })),
    ...params,
  })

export const useLoadServiceMetrics = (
  serviceId: number,
  kind: string,
  timeframe?: string,
  step?: number,
  end?: string,
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchServiceMetrics', serviceId, kind, timeframe],
    queryFn: async () =>
      serviceService.getServiceMetrics(serviceId, kind, timeframe, step),
    select: (data) => data.data,
    ...params,
    retry: false,
  })

const SERVICES_METRICS_CHUNK_SIZE = 100

const chunkServiceIds = (serviceIds: number[], size: number): number[][] => {
  const chunks: number[][] = []
  for (let i = 0; i < serviceIds.length; i += size) {
    chunks.push(serviceIds.slice(i, i + size))
  }
  return chunks
}

export const useLoadServiceComponentsMetrics = (
  serviceComponentIds: number[],
  kinds: MetricServiceKind[],
  timeframe: string,
  maxPoints: number,
  params?: GenericObject
) => {
  const chunks = useMemo(
    () =>
      chunkServiceIds(
        [...serviceComponentIds].sort((a, b) => a - b),
        SERVICES_METRICS_CHUNK_SIZE
      ),
    [serviceComponentIds]
  )

  const queries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: [
        'fetchServiceComponentsMetrics',
        kinds,
        timeframe,
        maxPoints,
        chunk,
      ],
      queryFn: () =>
        serviceService.getServiceComponentsMetrics({
          service_component_ids: chunk,
          kinds,
          timeframe,
          max_points: maxPoints,
        }),
      select: (response: AxiosResponse<BatchComponentMetricsSeriesResponse>) =>
        response.data.data,
      retry: false,
      ...params,
    })),
  })

  const isLoading = queries.some((query) => query.isLoading)

  const seriesByComponent = useMemo(() => {
    const map = new Map<number, Record<string, [number, string][]>>()
    for (const query of queries) {
      for (const series of query.data ?? []) {
        const entry = map.get(series.service_component_id) ?? {}
        entry[series.kind] = series.values
        map.set(series.service_component_id, entry)
      }
    }
    return map
  }, [queries])

  return { seriesByComponent, isLoading }
}

export const useLoadServicesMetrics = (
  serviceIds: number[],
  kinds: MetricServiceKind[],
  timeframe: string,
  maxPoints: number,
  params?: GenericObject
) => {
  const chunks = useMemo(
    () =>
      chunkServiceIds(
        [...serviceIds].sort((a, b) => a - b),
        SERVICES_METRICS_CHUNK_SIZE
      ),
    [serviceIds]
  )

  const queries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: ['fetchServicesMetrics', kinds, timeframe, maxPoints, chunk],
      queryFn: () =>
        serviceService.getServicesMetrics({
          service_ids: chunk,
          kinds,
          timeframe,
          max_points: maxPoints,
        }),
      select: (response: AxiosResponse<BatchMetricsSeriesResponse>) =>
        response.data.data,
      retry: false,
      ...params,
    })),
  })

  const isLoading = queries.some((query) => query.isLoading)

  const seriesByService = useMemo(() => {
    const map = new Map<number, Record<string, [number, string][]>>()
    for (const query of queries) {
      for (const series of query.data ?? []) {
        const entry = map.get(series.service_id) ?? {}
        entry[series.kind] = series.values
        map.set(series.service_id, entry)
      }
    }
    return map
  }, [queries])

  return { seriesByService, isLoading }
}

export const useLoadServiceMetricTypes = (params: {
  product_id?: string | undefined
  service_id?: number | undefined
}) =>
  useQuery({
    queryKey: [
      'useLoadServiceMetricTypes',
      params.product_id,
      params.service_id,
    ],
    queryFn: async () =>
      serviceService.getServiceMetricTypes(
        params.product_id,
        params.service_id
      ),
    select: (data) => data.data,
    retry: false,
    enabled: false,
  })

export const useLoadServiceMetricTypesGrouped = (
  params: {
    product_id?: string | undefined
    service_id?: number | undefined
    service_component_id?: number | undefined
  },
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [
      'useLoadServiceMetricTypesGrouped',
      params.product_id,
      params.service_id,
      params.service_component_id,
    ],
    queryFn: async () =>
      serviceService.getServiceMetricTypesGrouped(
        params.product_id,
        params.service_id,
        params.service_component_id
      ),
    select: (data) => data.data,
    retry: false,
    enabled: options?.enabled ?? false,
  })

export const useLoadAsSetSize = (sizes: string[]) =>
  useQuery({
    queryKey: ['getAsSetSize'],
    queryFn: async () => serviceService.getAsSetSize(sizes),
    retry: 2,
    refetchOnWindowFocus: false,
    select: (data) => data.data,
    enabled: false,
  })

export const useLoadAsnStatistics = (params?: GenericObject) =>
  useQuery({
    queryKey: ['fetchAsnStatistics'],
    queryFn: async () => {
      try {
        return await serviceService.getAsnStatistics()
      } catch (error) {
        console.error('Error fetching ASN statistics:', error) // eslint-disable-line
        return { data: [] }
      }
    },
    select: (data) => data?.data ?? [],
    ...params,
    retry: false,
  })

export const useLoadBgpSessionsStats = (
  serviceId: string | number,
  params?: GenericObject
) =>
  useQuery({
    queryKey: ['fetchBgpSessionsStats', serviceId],
    queryFn: async () => serviceService.getBgpSessionsStats(serviceId),
    meta: {
      error: { message: 'Could not load BGP sessions stats' },
    },
    select: (data) => data.data,
    retry: false,
    ...params,
  })
